import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import 'bootstrap/dist/css/bootstrap.min.css';
import { ToastContainer, toast } from 'react-toastify';
import CreatableSelect from 'react-select/creatable';
import 'react-toastify/dist/ReactToastify.css';
import countryLookup from '../utils/countryLookup';
import './AddOrEditPurchase.css';
import debounce from 'lodash.debounce';

const STORE_API_URL = process.env.REACT_APP_API_URL;

function AddOrEditPurchase({ isEdit }) {
  const { id, store_id } = useParams();
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [purchase, setPurchase] = useState({
    id: '',
    amount: '',
    currency: 'usd',
    status: 'succeeded',
    email: '',
    name: '',
    shipping: {
      address: {
        city: '',
        country: '',
        line1: '',
        line2: '',
        postal_code: '',
        state: ''
      },
      name: '',
      notes: '' 
    },
    stripe_payment_intent: '',
    stripe_payment_created: '',
    items: [],
    shipping_status: '',
    crt_models: [],
    custom_fields: []
  });

  const [modelInput, setModelInput] = useState('');
  const [crtSuggestions, setCrtSuggestions] = useState([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);

  useEffect(() => {
    const fetchProducts = async (storeId) => {
      console.log ('Fetching products for store: ' + storeId);
      try {
        // TODO: What if the user has more than 100 products?
        // Problem to solve for later
        const response = await fetch(`${STORE_API_URL}/products/store/${storeId}?type=sale&limit=100`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
        });
        const data = await response.json();
        //console.log ('Product data : ' + JSON.stringify(data));
        
        const filteredProducts = data.products.filter(product => product.type !== 'affiliate');
        //console.log ('Filtered Product data : ' + JSON.stringify(filteredProducts));

        setProducts(filteredProducts);
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };

    const fetchPurchase = async () => {
      console.log ('Fetching purchase.. Edit? ', isEdit);
      if (isEdit) {
        try {
          const response = await fetch(`${STORE_API_URL}/purchases/${id}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
          });
          const data = await response.json();

          console.log ('purchase data : ' + JSON.stringify(data));

          fetchProducts(data.store_id);
          setPurchase({
            ...data,
            custom_fields: data.custom_fields || [],
            items: data.items || []
          });
        } catch (error) {
          console.error('Error fetching purchase details:', error);
        }
      }
    };

    if (token) {
      fetchPurchase();
    }
  }, [isEdit, id, token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const [section, field] = name.split('.');

    // Handle nested fields for shipping object
    if (name.includes('.')) {
      const [section, field] = name.split('.');

      if (section === 'shipping') {
        setPurchase((prevPurchase) => ({
          ...prevPurchase,
          shipping: {
            ...prevPurchase.shipping,
            [field]: value || '' // Update the nested shipping field
          },
        }));
      } else if (section === 'address') {
        setPurchase((prevPurchase) => ({
          ...prevPurchase,
          shipping: {
            ...prevPurchase.shipping,
            address: {
              ...prevPurchase.shipping.address,
              [field]: value || '' // Update the nested address field
            },
          },
        }));
      }
    } else {
      // Handle top-level fields
      setPurchase((prevPurchase) => ({
        ...prevPurchase,
        [name]: value || '' // Ensure value is not null
      }));
    }
  };

  // This function adjusts the quantity directly from the quantity input field.
  const handleQuantityChange = (productId, quantity) => {
    setPurchase(prevPurchase => {
      const items = prevPurchase.items.map(item =>
        item.product_id === productId ? { ...item, quantity: Math.max(quantity, 0) } : item
      );
      return {
        ...prevPurchase,
        items,
      };
    });
  };

  // This function will now check if an item is already in the list, and if so, 
  // it will allow the user to change the quantity instead of only adding or removing the item.
  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;

    console.log ('value: ' + value + ' checked: ' + checked);
    const selectedProduct = products.find(product => product._id === value);

    console.log ('selectedProduct: ' + JSON.stringify(selectedProduct));
  
    setPurchase((prevPurchase) => {
      let newItem = {
        product_id: selectedProduct._id || null,
        price: selectedProduct.price,
        amount_total: selectedProduct.price,
        //amount_discount: 0,
        //amount_subtotal: selectedProduct.price,
        //amount_tax: 0,
        currency: selectedProduct.currency,
        description: selectedProduct.name,
        stripe_product_id: selectedProduct.stripe_product_id || null,
        hs_code: selectedProduct.hs_code || "",  // Add HS Code
        country_of_origin: selectedProduct.country_of_origin || "",  // Add Country of Origin
        quantity: 1
      };
  
      const existingItemIndex = prevPurchase.items.findIndex(item => item.product_id === value);

      console.log('newItem: ' + JSON.stringify(newItem));
      console.log('existingItemIndex: ' + existingItemIndex);

      if (existingItemIndex !== -1) {
        newItem = { ...prevPurchase.items[existingItemIndex] };
        newItem.quantity = checked ? newItem.quantity + 1 : newItem.quantity - 1;
        newItem.quantity = Math.max(newItem.quantity, 0); // Prevent negative quantities
      }
  
      const items = existingItemIndex !== -1
        ? [
            ...prevPurchase.items.slice(0, existingItemIndex),
            ...(newItem.quantity > 0 ? [newItem] : []),
            ...prevPurchase.items.slice(existingItemIndex + 1)
          ]
        : [...prevPurchase.items, newItem];

      console.log ('items: ' + JSON.stringify(items));

      return {
        ...prevPurchase,
        items,
      };
    });
  };  

  const handleItemFieldChange = (productId, field, value) => {
    setPurchase((prevPurchase) => {
      const items = prevPurchase.items.map(item =>
        item.product_id === productId ? { ...item, [field]: value } : item
      );
      return { ...prevPurchase, items };
    });
  };

  const handleCustomFieldChange = (index, e) => {
    const { name, value } = e.target;
    setPurchase((prevPurchase) => {
      const newCustomFields = [...prevPurchase.custom_fields];
      if (name.includes('.')) {
        const [section, field] = name.split('.');
        newCustomFields[index] = {
          ...newCustomFields[index],
          [section]: {
            ...newCustomFields[index][section],
            [field]: value || '' // Ensure value is not null
          }
        };
      } else {
        newCustomFields[index] = {
          ...newCustomFields[index],
          [name]: value || '' // Ensure value is not null
        };
      }
      return {
        ...prevPurchase,
        custom_fields: newCustomFields,
      };
    });
  };

  const handleAddCustomField = () => {
    setPurchase((prevPurchase) => ({
      ...prevPurchase,
      custom_fields: [
        ...prevPurchase.custom_fields,
        {
          key: '',
          label: {
            custom: '',
            type: 'custom'
          },
          optional: true,
          text: {
            default_value: null,
            maximum_length: null,
            minimum_length: null,
            value: ''
          },
          type: 'text'
        }
      ]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    //const userConfirmed = window.confirm('Are you sure you want to update?');
    //if (userConfirmed) {

      console.log ('Purchase id: ' + id);
      console.log ('Purchase object submitted: ' + JSON.stringify(purchase));

      try {
        const method = isEdit ? 'PUT' : 'POST';
        const endpoint = isEdit ? `${STORE_API_URL}/purchases/${id}` : `${STORE_API_URL}/purchases/store/${store_id}`;
        const response = await fetch(endpoint, {
          method: method,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(purchase),
        });

        const responseData = await response.json(); // Parse the response data

        if (!response.ok) {
          const errorMessage = responseData.error || 'Error saving product'; // Use the error message from backend or fallback to a generic one
          throw new Error(errorMessage);
        }

        if (!isEdit) {
          if (responseData.purchase_id) {
            navigate(`/edit-purchase/${responseData.purchase_id}`); // Navigate to the product page after adding a new product
          } else {
            navigate(`/purchases`);
          }
        }

        console.log('Response message: ' + JSON.stringify(responseData));

        toast.success(responseData.message || 'Purchase saved successfully!'); // Show success message from backend or fallback
      } catch (error) {
        toast.error(error.message); // Show error message in toast
      }
    //}
  };

  const purchasedProductIds = purchase.items.map(item => item.product_id);

  const debouncedFetch = useCallback(
    debounce(async (inputValue) => {
      if (inputValue.length < 2) {
        setCrtSuggestions([]);
        return;
      }
  
      setIsLoadingModels(true);
      try {
        const res = await fetch(`${STORE_API_URL}/crts/autocomplete?q=${encodeURIComponent(inputValue)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        const formatted = (data.suggestions || []).map((item) => ({
          value: item.title,
          label: item.title,
          slug: item.slug || null,
          _id: item._id || null,
        }));
        setCrtSuggestions(formatted);
      } catch (err) {
        console.error('CRT autocomplete error:', err);
      } finally {
        setIsLoadingModels(false);
      }
    }, 500),
    [token]
  );
  
  useEffect(() => {
    return () => debouncedFetch.cancel();
  }, [debouncedFetch]);
  

  return (
    <div className="container">
      <ToastContainer />
      <div className="headline d-flex justify-content-between align-items-center">
        <h2>{isEdit ? 'Edit Purchase' : 'Add New Purchase'}</h2>
      </div>
      <h6><Link to={`/purchases`}>Back to Purchases</Link></h6>
      <div className="mt-3">
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Stripe Payment Intent:</label>
            <input type="text" className="form-control" name="amount" value={purchase.stripe_payment_intent || ''} disabled/>
          </div>
          <div className="form-group">
            <label>Amount:</label>
            <input type="text" className="form-control" name="amount" value={purchase.amount || ''} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Currency:</label>
            <input type="text" className="form-control" name="currency" value={purchase.currency || ''} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Email:</label>
            <input type="email" className="form-control" name="email" value={purchase.email || ''} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Name:</label>
            <input type="text" className="form-control" name="name" value={purchase.name || ''} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Shipping Address Line 1:</label>
            <input type="text" className="form-control" name="address.line1" value={(purchase.shipping && purchase.shipping.address && purchase.shipping.address.line1) || ''} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Shipping Address Line 2:</label>
            <input type="text" className="form-control" name="address.line2" value={(purchase.shipping && purchase.shipping.address && purchase.shipping.address.line2) || ''} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>City:</label>
            <input type="text" className="form-control" name="address.city" value={(purchase.shipping && purchase.shipping.address && purchase.shipping.address.city) || ''} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>State:</label>
            <input type="text" className="form-control" name="address.state" value={(purchase.shipping && purchase.shipping.address && purchase.shipping.address.state) || ''} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Postal Code:</label>
            <input type="text" className="form-control" name="address.postal_code" value={(purchase.shipping && purchase.shipping.address && purchase.shipping.address.postal_code) || ''} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Country:</label>
            <select
              className="form-select"
              name="address.country"
              value={purchase.shipping?.address?.country || ''}
              onChange={handleChange}
            >
              <option value="">Select Country</option>
              {Object.entries(countryLookup).map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Shipping Status:</label>
            <select
              className="form-select"
              name="shipping_status"
              value={purchase.shipping_status || ''}
              onChange={handleChange}
            >
              <option value="">Select Shipping Status</option>
              <option value="processing">Processing</option>
              <option value="label_created">Label Created</option>
              <option value="in_transit">In Transit</option>
              <option value="delivered">Delivered</option>
              <option value="suspended">Suspended</option>
              <option value="returned">Returned</option>
              <option value="canceled">Canceled</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div className="form-group">
            <label>Shipping Notes:</label>
            <textarea
              className="form-control"
              name="shipping.notes"
              value={purchase.shipping?.notes || ''}
              onChange={handleChange}
            ></textarea>
          </div>
          {isEdit &&
          <>
            <div className="form-group">
              <label>Items:</label>
              <div className="checkbox-group">
                {products.map(product => (
                  <div key={product._id} className="checkbox-item mb-1">
                    <input
                      className='form-check-input me-2'
                      type="checkbox"
                      value={product._id}
                      onChange={handleCheckboxChange}
                      checked={purchasedProductIds.includes(product._id)}
                    />
                    {product.name}
                    <div style={{ width: '100px' }}>
                    {purchasedProductIds.includes(product._id) && (
                      <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                        <input
                          type="number"
                          className="form-control" 
                          min="0"
                          style={{ width: "80px" }} 
                          value={purchase.items.find(item => item.product_id === product._id)?.quantity || 0}
                          onChange={(e) => handleQuantityChange(product._id, parseInt(e.target.value))}
                        />

                        {/* HS Code Input */}
                        <input
                          type="text"
                          className="form-control"
                          placeholder="HS Code"
                          style={{ flex: "1", minWidth: "150px" }}
                          value={purchase.items.find(item => item.product_id === product._id)?.hs_code || ""}
                          onChange={(e) => handleItemFieldChange(product._id, 'hs_code', e.target.value)}
                        />

                        {/* Country of Origin Input */}
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Country of Origin"
                          style={{ flex: "1", minWidth: "150px" }}
                          value={purchase.items.find(item => item.product_id === product._id)?.country_of_origin || ""}
                          onChange={(e) => handleItemFieldChange(product._id, 'country_of_origin', e.target.value)}
                        />
                      </div>
                    )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>CRT Model (for reference)</label>
              <CreatableSelect
                isMulti
                styles={{ container: (provided) => ({ ...provided, width: '100%' }) }}
                isClearable
                isSearchable
                isLoading={isLoadingModels}
                options={crtSuggestions}
                value={purchase.crt_models && purchase.crt_models.map(model => ({
                  value: model.model,
                  label: model.model,
                  slug: model.slug || null,
                  _id: model._id || null
                }))}
                onInputChange={(inputValue, { action }) => {
                  if (action === 'input-change') {
                    setModelInput(inputValue);
                    debouncedFetch(inputValue);
                  }
                  if (action === 'menu-close') {
                    setModelInput('');
                  }
                }}
                inputValue={modelInput}
                onChange={(selected) => {
                  setPurchase((prev) => ({
                    ...prev,
                    crt_models: selected.map(opt => ({
                      model: opt.value,
                      slug: opt.slug || null,
                      _id: opt._id || null,
                    }))
                  }));
                }}
                placeholder="Search CRT model..."
                formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                classNamePrefix="dark-select"
              />
            </div>
            {purchase.crt_models?.length > 0 && (
              <div className="form-group">
                <label>CRT Details</label>
                <div style={{ width: '100%' }}>
                  {purchase.crt_models.map((crt, idx) =>
                    crt.slug ? (
                      <div key={idx}>
                        <small>
                          <a
                            href={`/crts/${crt.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {window.location.origin}/crts/{crt.slug}
                          </a>
                        </small>
                      </div>
                    ) : null
                  )}
                </div>
              </div>
            )}
          </>
          }
          {isEdit &&
            <div className="form-group">
              <label>Custom Fields:</label>
              <div className="custom-field-group">
                {(purchase.custom_fields || []).map((field, index) => (
                  <div key={index}>
                    <div className="mb-2">
                      <input
                        type="text"
                        name="key"
                        className="form-control" 
                        placeholder="Key"
                        value={field.key || ''}
                        onChange={(e) => handleCustomFieldChange(index, e)}
                      />
                    </div>
                    <div className="mb-2">
                      <input
                        type="text"
                        name="label.custom"
                        className="form-control" 
                        placeholder="Title"
                        value={field.label?.custom || ''}
                        onChange={(e) => handleCustomFieldChange(index, e)}
                      />
                    </div>
                    <div className="mb-2">
                      <input
                        type="text"
                        name="text.value"
                        className="form-control" 
                        placeholder="Value"
                        value={field.text?.value || ''}
                        onChange={(e) => handleCustomFieldChange(index, e)}
                      />
                    </div>
                  </div>
                ))}
                <button className="btn btn-primary mt-2" type="button" onClick={handleAddCustomField}>+</button>
              </div>
            </div>
          }
          <button className="btn btn-primary" type="submit">{isEdit ? 'Update Purchase' : 'Add Purchase'}</button>
        </form>
      </div>
    </div>
  );
}

export default AddOrEditPurchase;
